<template>
  <div class="filter-chip" :class="{ 'is-removable': canRemove }">
    <button
      type="button"
      class="filter-chip__btn"
      :class="{ 'is-on': active, 'filter-chip__btn--text': isText }"
      v-tooltip="{ content: tooltip || '', placement: 'top', delay: 400 }"
      :aria-label="ariaLabel"
      :aria-haspopup="hasPopup ? 'true' : null"
      :aria-expanded="hasPopup ? String(open) : null"
      @click.stop="$emit('toggle', $event)"
      @keydown.esc="$emit('close')"
    >
      <BaseIcon v-if="icon" :name="icon" class="filter-chip__ico" />
      <!-- An empty label renders nothing so an icon chip with no selection stays icon-only. -->
      <span v-if="isText && label" class="filter-chip__text">{{ label }}</span>
      <span v-else-if="badge" class="filter-chip__badge">{{ badge }}</span>
      <BaseIcon
        v-if="(hasPopup || opensPanel) && !canRemove"
        name="chevron-down"
        class="filter-chip__car"
        :class="{ 'is-open': open }"
      />
    </button>
    <button
      v-if="canRemove"
      type="button"
      class="filter-chip__remove"
      :aria-label="$t('filters.remove', { name: popTitle || tooltip || label })"
      @click.stop="$emit('clear')"
    >
      <BaseIcon name="x" />
    </button>

    <!-- :duration is an explicit safety net: Vue removes the element on a timer, so a rapid
         re-render interrupting the leave can't leave the popover stuck open. On mobile the popover
         becomes a bottom sheet (full-width, viewport-anchored) so options never clip off-screen. -->
    <transition :name="isMobile ? 'filter-chip-sheet' : 'filter-chip-pop'" :duration="isMobile ? 240 : 120">
      <div
        v-if="open && hasPopup"
        v-closable="onClose"
        class="filter-chip__pop"
        :class="{
          'filter-chip__pop--wide': wide,
          'filter-chip__pop--date': dateVariant,
          'filter-chip__pop--sheet': isMobile,
          'filter-chip__pop--flip-right': flipRight,
          'filter-chip__pop--flip-up': flipUp,
          'is-sheet-dragging': dragging,
        }"
        :style="popStyle"
        @click.stop
        @keydown.esc="$emit('close')"
        @touchstart="onSheetTouchStart"
        @touchmove="onSheetTouchMove"
        @touchend="onSheetTouchEnd"
      >
        <div v-if="isMobile" class="filter-chip__sheet-head">
          <span class="filter-chip__sheet-grabber" aria-hidden="true"></span>
          <div v-if="popTitle || label || $scopedSlots['title-action']" class="filter-chip__sheet-title-row">
            <span v-if="popTitle || label" class="filter-chip__sheet-title">{{ popTitle || label }}</span>
            <div v-if="$scopedSlots['title-action']" class="filter-chip__title-action">
              <slot name="title-action" />
            </div>
          </div>
        </div>
        <div v-else-if="popTitle || $scopedSlots['title-action']" class="filter-chip__pop-title">
          <span v-if="popTitle" class="filter-chip__pop-title-text">{{ popTitle }}</span>
          <div v-if="$scopedSlots['title-action']" class="filter-chip__title-action">
            <slot name="title-action" />
          </div>
        </div>
        <div class="filter-chip__pop-body">
          <slot />
        </div>

        <!-- Mobile: sticky footer so Effacer/Terminé stay reachable even when the list fills the
             screen. Filters apply live, so "Terminé" just dismisses the sheet. -->
        <div v-if="isMobile" class="filter-chip__sheet-foot">
          <BaseButton
            v-if="showClear"
            class="filter-chip__sheet-clear"
            isTransparent
            isUnderline
            isSquare
            @click.native="$emit('clear')"
          >
            {{ $t('filters.clear') }}
          </BaseButton>
          <BaseButton class="filter-chip__sheet-done" @click.native="$emit('close')">
            {{ $t('filters.done') }}
          </BaseButton>
        </div>
        <BaseButton
          v-else-if="showClear"
          class="filter-chip__pop-clear"
          isSmall
          isTransparent
          isSquare
          @click.native="$emit('clear')"
        >
          {{ $t('filters.clear') }}
        </BaseButton>
      </div>
    </transition>

    <!-- Dimmed backdrop behind the mobile bottom sheet; tapping it closes the sheet. -->
    <transition name="filter-chip-scrim">
      <div v-if="open && hasPopup && isMobile" class="filter-chip__scrim" @click="$emit('close')"></div>
    </transition>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import { closeOnBack } from '@/util/back-button';

// Drag the mobile sheet down past this many pixels to dismiss it; otherwise it snaps back.
const SHEET_DISMISS_THRESHOLD = 90;

export default {
  name: 'FilterChip',
  mixins: [IsMobileMixin],
  components: { BaseButton, BaseIcon },
  // A row of the filters in use (the phone's) gives each chip a × that clears it.
  inject: { removable: { from: 'filterChipsRemovable', default: false } },
  props: {
    // Chip face
    label: { type: String, default: '' },
    icon: { type: String, default: null },
    isText: { type: Boolean, default: false },
    badge: { type: String, default: null },
    tooltip: { type: String, default: null },
    active: { type: Boolean, default: false },
    // Popover
    hasPopup: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    popTitle: { type: String, default: null },
    wide: { type: Boolean, default: false },
    dateVariant: { type: Boolean, default: false },
    showClear: { type: Boolean, default: false },
    // Opens a panel of its own (a drawer, a dialog) instead of the popover: the chevron says so.
    opensPanel: { type: Boolean, default: false },
  },
  data() {
    return {
      // Mobile bottom-sheet swipe-to-dismiss state.
      dragStartY: null,
      dragY: 0,
      dragging: false,
      canDrag: false,
      // Desktop popover placement: flip left/up when the default down-right anchor would clip.
      flipRight: false,
      flipUp: false,
      // Height cap (px) so a tall list scrolls internally instead of clipping in a short viewport.
      maxPopHeight: null,
    };
  },
  computed: {
    canRemove() {
      return this.removable && this.active;
    },
    sheetOpen() {
      return this.open && this.hasPopup && this.isMobile;
    },
    // Accessible name for the chip button. An aria-label REPLACES the button's visible text, so
    // combine the dimension (tooltip, set in collapse/value modes) with the visible face text —
    // otherwise a screen reader would hear only the dimension and miss the current selection.
    // Full mode has no tooltip and shows the whole "dimension : value" on the face, so let that
    // visible text be the name (return null).
    ariaLabel() {
      const dim = this.tooltip;
      const face = this.isText ? this.label : null;
      if (!dim) return null;
      return face && face !== dim ? `${dim} : ${face}` : dim;
    },
    // Merge the mobile drag transform with the desktop height cap into one style binding.
    popStyle() {
      const style = {};
      if (this.dragY) style.transform = 'translateY(' + this.dragY + 'px)';
      if (!this.isMobile && this.maxPopHeight) style.maxHeight = this.maxPopHeight + 'px';
      return Object.keys(style).length ? style : null;
    },
  },
  methods: {
    releaseBackButton() {
      if (this.releaseBack) this.releaseBack();
      this.releaseBack = null;
    },
    // --- Mobile bottom-sheet: swipe down to dismiss --------------------------------------------
    onSheetTouchStart(event) {
      if (!this.isMobile) return;
      const body = this.$el.querySelector('.filter-chip__pop-body');
      // Only drag the sheet from the header, or when the option list is already scrolled to the
      // top — otherwise a downward swipe should scroll the list, not move the sheet.
      const fromHead = event.target.closest && event.target.closest('.filter-chip__sheet-head');
      this.canDrag = !!fromHead || !body || body.scrollTop <= 0;
      this.dragStartY = event.touches[0].clientY;
      this.dragY = 0;
    },
    onSheetTouchMove(event) {
      if (!this.isMobile || this.dragStartY === null || !this.canDrag) return;
      const delta = event.touches[0].clientY - this.dragStartY;
      if (delta <= 0) {
        // Upward (or no) movement — hand control back to the list.
        this.dragging = false;
        this.dragY = 0;
        return;
      }
      this.dragging = true;
      this.dragY = delta;
      // Stop the list from scrolling while the sheet follows the finger.
      if (event.cancelable) event.preventDefault();
    },
    onSheetTouchEnd() {
      if (!this.isMobile || this.dragStartY === null) return;
      const shouldClose = this.dragY > SHEET_DISMISS_THRESHOLD;
      this.dragging = false;
      this.dragY = 0; // snap back (CSS transitions transform) or clear before the leave transition
      this.dragStartY = null;
      this.canDrag = false;
      if (shouldClose) this.$emit('close');
    },
    // v-closable requires a method name (it resolves vnode.context[expression]).
    onClose(event) {
      // Ignore mousedown on our own chip button: otherwise clicking the chip while open would
      // close here (mousedown) and then re-open via the button's click. Let the button toggle it.
      const btn = this.$el && this.$el.querySelector('.filter-chip__btn');
      if (btn && event && btn.contains(event.target)) return;
      this.$emit('close');
    },
    // --- Desktop popover placement ------------------------------------------------------------
    // Detect fixed/sticky chrome pinned to the top or bottom of the viewport (a sticky header, a
    // floating footer) so placement measures the *visible* band rather than the raw viewport —
    // otherwise a flipped-up popover renders under the header. Returns { top, bottom } y-bounds.
    viewportInsets() {
      const h = window.innerHeight;
      const x = Math.round(window.innerWidth / 2);
      let top = 0;
      let bottom = h;
      const pinnedAt = (y) =>
        (document.elementsFromPoint(x, Math.min(Math.max(y, 0), h - 1)) || []).filter((el) => {
          if (!el || (this.$el && this.$el.contains(el))) return false;
          const pos = getComputedStyle(el).position;
          return pos === 'fixed' || pos === 'sticky';
        });
      for (const y of [2, 24, 48, 72]) {
        for (const el of pinnedAt(y)) {
          const r = el.getBoundingClientRect();
          if (r.top <= 4 && r.bottom > top && r.bottom < h * 0.5) top = r.bottom;
        }
      }
      for (const y of [h - 2, h - 24, h - 48, h - 72]) {
        for (const el of pinnedAt(y)) {
          const r = el.getBoundingClientRect();
          if (r.bottom >= h - 4 && r.top < bottom && r.top > h * 0.5) bottom = r.top;
        }
      }
      return { top, bottom };
    },
    // Flip the popover left (right-aligned) or up when the default down-right anchor would spill
    // past the viewport, so it never clips off-screen. The mobile bottom sheet is full-width and
    // viewport-anchored, so it never needs this.
    updatePlacement() {
      if (this.isMobile) {
        this.flipRight = false;
        this.flipUp = false;
        this.maxPopHeight = null;
        return;
      }
      // Reset to the default placement first so we always measure against the un-flipped anchor.
      this.flipRight = false;
      this.flipUp = false;
      this.maxPopHeight = null;
      this.$nextTick(() => {
        const pop = this.$el && this.$el.querySelector('.filter-chip__pop');
        const btn = this.$el && this.$el.querySelector('.filter-chip__btn');
        if (!pop || !btn) return;
        const rect = pop.getBoundingClientRect();
        const chip = btn.getBoundingClientRect();
        const margin = 8;
        const gap = 6;
        // Horizontal: right-align if the default left-anchored popover overflows the right edge.
        this.flipRight = rect.right > window.innerWidth - margin;
        // Vertical: keep the popover on whichever side of the chip has more room, and cap its
        // height to that room so it scrolls internally rather than clipping off the top or bottom
        // in a short viewport (flipping up alone just moves the clip from bottom to top). Measure
        // against the *visible* band (excluding sticky-header / floating-footer chrome) so a
        // flipped-up popover doesn't disappear under the header.
        const insets = this.viewportInsets();
        const spaceBelow = insets.bottom - chip.bottom - gap - margin;
        const spaceAbove = chip.top - insets.top - gap - margin;
        const fitsBelow = rect.height <= spaceBelow;
        this.flipUp = !fitsBelow && spaceAbove > spaceBelow;
        const avail = this.flipUp ? spaceAbove : spaceBelow;
        // Only constrain when the natural popover wouldn't fit; cap strictly to the available space
        // (never a min-height floor above it, or the popover would spill back under the chrome).
        this.maxPopHeight = rect.height > avail ? Math.max(0, Math.floor(avail)) : null;
      });
    },
    onResize() {
      if (this.open) this.updatePlacement();
    },
  },
  watch: {
    sheetOpen: {
      immediate: true,
      handler(isOpen) {
        this.releaseBackButton();
        if (isOpen) this.releaseBack = closeOnBack(() => this.$emit('close'));
      },
    },
    open(isOpen) {
      if (isOpen) {
        this.updatePlacement();
      } else {
        this.flipRight = false;
        this.flipUp = false;
        this.maxPopHeight = null;
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
  },
  // Leaving the page closes the sheet, as it does a dialog.
  deactivated() {
    if (this.sheetOpen) this.$emit('close');
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.releaseBackButton();
  },
};
</script>

<style lang="scss">
.filter-chip {
  // Wrapper anchors the popover (a sibling of the button) so the button can be a
  // real <button> without nesting interactive popover content inside it.
  position: relative;
  display: inline-flex;

  &__btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: $color-white;
    border: 1px solid transparent;
    box-shadow: $shadow-sm;
    border-radius: $radius-md;
    padding: 7px 11px;
    // Fixed control height so text and icon-only chips are identical — otherwise the text
    // line-box (~16px) is 1px taller than the 15px icon, making hover/active backgrounds uneven.
    min-height: 32px;
    font-family: inherit;
    font-size: 13px;
    font-weight: bold;
    color: $color-text-body;
    cursor: pointer;
    transition: background ease 100ms;

    &:hover {
      background: $color-hover;
    }

    &.is-on {
      background: $color-accent-100;
      outline: 1.5px solid $color-accent-300;
      color: $color-accent;

      &:hover {
        background: $color-accent-200;
      }
    }
  }

  &__ico {
    width: 15px;
    height: 15px;
  }

  &__badge {
    font-size: 11px;
    font-weight: bold;
  }

  &__car {
    width: 13px;
    height: 13px;
    color: $color-slate-400;
    transition: transform 150ms ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__btn.is-on &__car {
    color: $color-accent;
  }

  // The × sits inside the chip's right end, as its own button.
  &.is-removable &__btn {
    padding-right: 36px;
  }

  &__remove {
    position: absolute;
    top: 50%;
    right: 2px;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    padding: 0;
    transform: translateY(-50%);
    border: none;
    border-radius: $radius-md;
    background: none;
    color: $color-accent;
    cursor: pointer;

    .base-icon {
      width: 15px;
      height: 15px;
    }
  }

  &__pop {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 40;
    min-width: 220px;
    max-height: 320px;
    // Flex column so the option body scrolls while the title and clear stay pinned.
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    box-shadow: $shadow-dropdown;
    padding: 8px;
    // Reset colour so the chip's active (accent) colour doesn't cascade into items.
    color: $color-text-body;
    font-weight: normal;

    // Wide variant (e.g. platforms: icon + name + tags on full-width rows).
    &--wide {
      min-width: 360px;

      .form-checkbox,
      .form-checkbox__label {
        width: 100%;
      }
    }

    // Match the checkbox rows to FilterRadioList rows for parity: same row padding (8px 9px),
    // same control size as the radio dot (17px), same label size (13px).
    .form-checkbox {
      padding: 8px 9px;
      margin-right: 0;
    }

    .form-checkbox--small .form-checkbox__check {
      width: 17px;
      height: 17px;
    }

    .form-checkbox--small .form-checkbox__label {
      font-size: 13px;
    }
  }

  // Viewport-edge flips (desktop only): keep the popover on-screen near the right/bottom edges.
  // The default anchor is top:100% / left:0; these swap the axis that would otherwise clip.
  &__pop--flip-right {
    left: auto;
    right: 0;
  }

  &__pop--flip-up {
    top: auto;
    bottom: calc(100% + 6px);
  }

  // Scrollable option area; title and clear are fixed siblings above/below it.
  &__pop-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  &__pop-title {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 4px 8px 6px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-slate-400;
  }

  &__pop-title-text {
    min-width: 0;
  }

  &__title-action {
    flex: 0 0 auto;
    margin-left: auto;
    text-transform: none;
    letter-spacing: normal;
    font-weight: normal;
  }

  // Layout only — BaseButton owns the button styling. Full-width clear at the bottom
  // of the popover, separated from the option list by a divider.
  &__pop-clear {
    flex: 0 0 auto;
    display: block;
    width: 100%;
    margin-top: 4px;
    border-top: 1px solid $color-border;
    padding-top: 4px;
  }

  // --- Mobile bottom sheet ------------------------------------------------------------------
  // On mobile the popover becomes a full-width sheet pinned to the bottom of the viewport, so
  // option lists (incl. the wide platform list) never clip off-screen like an anchored dropdown.
  &__pop--sheet {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1001;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: 85vh;
    border: none;
    border-radius: $radius-md $radius-md 0 0;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
    padding: 8px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    // Snap-back when a swipe-to-dismiss is released below the threshold. The enter/leave
    // transitions (defined below) override this while opening/closing.
    transition: transform 220ms ease;

    // While the finger drives the sheet, follow it 1:1 (no easing).
    &.is-sheet-dragging {
      transition: none;
    }
  }

  // Keep the sheet sizing for the date variant (which otherwise drops max-height / padding).
  &__pop--sheet.filter-chip__pop--date {
    max-height: 85vh;
    overflow-y: auto;
    padding: 8px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  }

  &__scrim {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: rgba(31, 41, 55, 0.4);
  }

  &__sheet-head {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 6px 0 10px;
  }

  &__sheet-grabber {
    width: 36px;
    height: 4px;
    border-radius: $radius-full;
    background: $color-slate-300;
  }

  &__sheet-title-row {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__sheet-title {
    min-width: 0;
    font-size: 14px;
    font-weight: bold;
    color: $color-text-body;
  }

  // Sticky action row pinned to the bottom of the sheet; the option list scrolls above it.
  &__sheet-foot {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    padding-top: 10px;
    border-top: 1px solid $color-border;
  }

  &__sheet-clear {
    flex: 0 0 auto;
  }

  // "Terminé" fills the remaining width for a large, thumb-reachable tap target.
  &__sheet-done {
    flex: 1 1 auto;
  }
}

// Subtle open/close: fade + small scale from the chip (top-left) anchor.
.filter-chip-pop-enter-active,
.filter-chip-pop-leave-active {
  transition: opacity 120ms ease, transform 120ms ease;
  transform-origin: top left;
}

.filter-chip-pop-enter,
.filter-chip-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

// Mobile bottom sheet: slide up from the bottom; the scrim fades in.
.filter-chip-sheet-enter-active,
.filter-chip-sheet-leave-active {
  transition: transform 240ms ease, opacity 240ms ease;
}

.filter-chip-sheet-enter,
.filter-chip-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0.5;
}

.filter-chip-scrim-enter-active,
.filter-chip-scrim-leave-active {
  transition: opacity 240ms ease;
}

.filter-chip-scrim-enter,
.filter-chip-scrim-leave-to {
  opacity: 0;
}

// Respect users who prefer reduced motion.
@media (prefers-reduced-motion: reduce) {
  .filter-chip-pop-enter-active,
  .filter-chip-pop-leave-active,
  .filter-chip-sheet-enter-active,
  .filter-chip-sheet-leave-active,
  .filter-chip-scrim-enter-active,
  .filter-chip-scrim-leave-active {
    transition: none;
  }
}
</style>
